import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { InvoiceOverviewTable } from "@/features/invoice-overview/invoiceOverviewTable";
import { useExportedInitialQuery } from "@/features/todos/initial-filters";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { InvoicesProvider } from "@/providers/invoices";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { InvoiceFilterFields } from "@/types/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export default function TodosExported({ type }: { type: InvoiceTypeString }) {
  const storageKey = useMemo(() => {
    return `todos-exported-${type}`;
  }, [type]);

  const { t } = useTranslation();
  const { page, setPage, limit, setLimit } = useTablePagination();
  const { query } = useExportedInitialQuery({ type });
  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    filters,
    setFilters,
    sorting,
    onSortingChange,
    sort,
    order,
    initialServerSideFilters,
    resetFilters,
  } = useServerSideQuery<InvoiceFilterFields>({
    storageKey,
    defaultFilters: query,
  });

  const columnVisibility = useMemo(
    () => ({
      type: false,
      invoiceNo: true,
      invoiceDate: true,
      startDate: true,
      expenseAccount: true,
      datevBu: true,
      tags: true,
      createdAt: true,
      status: false,
      reviewType: false,
      ocrReviewed: false,
      isBooking: false,
      isDuplicate: false,
    }),
    [],
  );

  const filtersConfig = useMemo(
    () => ({
      showStatusFilter: false,
      showUploadButton: false,
    }),
    [],
  );

  const actionsConfig = useMemo(
    () => ({
      showTypeUpdateButton: false,
    }),
    [],
  );

  const {
    data: invoices,
    error,
    loading,
    pagination,
  } = useGetAllInvoices({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    sort,
    order,
  });

  return (
    <div className="flex flex-col h-full gap-4">
      <UsageHelperBanner label={t("todos.exported_explainer")} />
      <div className="flex-1 min-h-0">
        <InvoicesProvider invoices={invoices} loading={loading}>
          <InvoiceOverviewTable
            tableName="exported"
            showUploadProgress={false}
            filtersConfig={filtersConfig}
            actionsConfig={actionsConfig}
            columnVisibility={columnVisibility}
            totalItems={pagination.totalItems}
            currentPage={page}
            totalPages={pagination.totalPages}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            filters={filters}
            setFilters={setFilters}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            sorting={sorting}
            onSortingChange={onSortingChange}
            error={error}
            readOnly
            invoiceType={type}
            initialServerSideFilters={initialServerSideFilters}
            globalServerSideSearch={globalSearch}
            serverSideFilters={serverSideFilters}
            resetFilters={resetFilters}
            emptyStateComponent={<NoRecordsEmpty state="no-records" />}
          />
        </InvoicesProvider>
      </div>
    </div>
  );
}
